import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { fetchAvailabilityOverview } from '@/lib/api';
import type { AvailabilityOverview, OverviewParams } from '@/lib/api-types';

export interface AvailabilityFilters {
  category?: string;
  city?: string;
  sku?: string;
}

export interface UseOverviewOptions {
  customerName: string;
  filters: AvailabilityFilters;
  /** Named preset or an explicit start/end; passed through to the API. */
  range?: { token?: string; start?: string; end?: string };
  trendDays?: number;
  limit?: number;
  enabled?: boolean;
}

/**
 * One query backs the whole page.
 *
 * `placeholderData: keepPrevious` is the important bit: drilling into a
 * category re-fetches all three tables, and without it every click would blank
 * the page and reflow 21 rows of sparklines. The user sees the previous data
 * dimmed instead, which is why the page never appears to hang.
 */
export function useAvailabilityOverview(
  opts: UseOverviewOptions,
): UseQueryResult<AvailabilityOverview, Error> {
  const params: OverviewParams = {
    customer_name: opts.customerName,
    window: opts.range ?? { token: 'last_7_days' },
    filters: opts.filters,
    trend_days: opts.trendDays ?? 7,
    limit: opts.limit ?? 6,
  };

  return useQuery({
    queryKey: ['availability-overview', params],
    queryFn: ({ signal }) => fetchAvailabilityOverview(params, signal),
    enabled: opts.enabled ?? true,
    // The underlying grain is daily, so minute-level freshness buys nothing
    // and costs a BigQuery scan per interaction.
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    placeholderData: (prev) => prev,
    retry: (failureCount, error) => {
      // A 4xx is a contract problem; retrying just delays the error.
      const status = (error as { status?: number }).status;
      if (status != null && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}
